<template>
  <div class="app-pdf-viewer">
    <div
      ref="pdfContainer"
      class="app-pdf-viewer__container" />
  </div>
</template>

<script>
import { loadEmbedPDF } from '@/utils/embedpdf';

export default {
  name: 'AppPDFViewer',
  props: {
    canEditPdf: {
      type: Boolean,
      default: false,
    },
    src: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      viewerElement: null,
    };
  },

  watch: {
    src() {
      const container = this.$refs.pdfContainer;
      if (container) {
        container.innerHTML = '';
      }
      this.viewerElement = null;
      this.initViewer();
    },
  },

  async mounted() {
    await this.initViewer();
  },

  beforeDestroy() {
    const container = this.$refs.pdfContainer;
    if (container) {
      container.innerHTML = '';
    }
    this.viewerElement = null;
  },

  methods: {
    async initViewer() {
      const container = this.$refs.pdfContainer;
      if (!container || !this.src) return;

      const vm = this;
      const EmbedPDF = await loadEmbedPDF();
      const viewerElement = EmbedPDF.init({
        type: 'container',
        target: container,
        src: this.src,
        disabledCategories: [
          'annotation',
          'annotations',
          'redaction',
          'document',
          'page',
          'panel',
          'history',
        ],
        responsive: false,
        permissions: {
          canModifyAnnotations: false,
        },
      });
      this.viewerElement = viewerElement;

      viewerElement.registry.then((registry) => {
        if (!vm.viewerElement || vm.viewerElement !== viewerElement) return;
        const commands = registry.getPlugin('commands').provides();
        const ui = registry.getPlugin('ui').provides();

        if (vm.canEditPdf) {
          commands.registerCommand({
            id: 'custom.edit-pdf',
            label: 'Edit PDF',
            action: () => vm.$emit('edit-pdf'),
          });
        }

        commands.registerCommand({
          id: 'custom.close',
          label: 'Close',
          action: () => vm.$emit('close'),
        });

        const schema = ui.getSchema();
        const mainToolbar = schema.toolbars?.['main-toolbar'];
        if (!mainToolbar) return;

        const customItems = [
          ...(vm.canEditPdf ? [{
            type: 'group',
            id: 'readonly-left-group',
            alignment: 'start',
            gap: 2,
            items: [
              {
                type: 'command-button',
                id: 'edit-pdf-button',
                commandId: 'custom.edit-pdf',
                variant: 'text',
              },
            ],
          }] : [
          ]),
          {
            type: 'spacer',
            id: 'readonly-spacer-left',
            flex: true,
          },
          {
            type: 'custom',
            id: 'zoom-toolbar',
            componentId: 'zoom-toolbar',
            categories: ['zoom'],
          },
          {
            type: 'spacer',
            id: 'readonly-spacer-right',
            flex: true,
          },
          {
            type: 'group',
            id: 'readonly-right-group',
            alignment: 'end',
            gap: 2,
            items: [
              {
                type: 'command-button',
                id: 'close-button',
                commandId: 'custom.close',
                variant: 'text',
              },
            ],
          },
        ];

        ui.mergeSchema({
          toolbars: {
            'main-toolbar': {
              ...mainToolbar,
              items: customItems,
              responsive: false,
            },
          },
        });
      }).catch(() => {
        // Registry may reject if element is removed before ready
      });
    },
  },
};
</script>

<style scoped lang="scss">
.app-pdf-viewer {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &__container {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
}
</style>
